import httpClient from "./httpClient";
import { generateIdempotencyKey, getPage } from "./apiClient";
import { onlyDigits } from "../utils/inputMasks";

function serializeListParam(values = []) {
  if (!Array.isArray(values) || values.length === 0) {
    return undefined;
  }

  const normalized = values
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  return normalized.length > 0 ? normalized.join(",") : undefined;
}

function normalizeParceiroResponse(parceiro = {}) {
  const usuarioId = parceiro.usuarioId ?? parceiro.usuario?.id ?? null;
  const usuarioNome = parceiro.usuarioNome ?? parceiro.usuario?.nome ?? "";
  const usuarioFotoPerfilUrl = parceiro.usuarioFotoPerfilUrl ?? parceiro.usuario?.fotoPerfilUrl ?? "";

  return {
    ...parceiro,
    bairro: parceiro.bairro ?? "",
    usuarioId,
    usuarioNome,
    usuarioFotoPerfilUrl,
    usuario: usuarioId || usuarioNome || usuarioFotoPerfilUrl
      ? {
          ...(parceiro.usuario || {}),
          id: usuarioId,
          nome: usuarioNome || parceiro.usuario?.nome || "",
          fotoPerfilUrl: usuarioFotoPerfilUrl || parceiro.usuario?.fotoPerfilUrl || "",
        }
      : parceiro.usuario,
  };
}

function normalizeParceiroPayload(parceiro = {}) {
  return {
    idJoin: parceiro.idJoin ?? null,
    nome: parceiro.nome ?? "",
    documento: onlyDigits(parceiro.documento ?? ""),
    endereco: parceiro.endereco ?? "",
    status: parceiro.status ?? "ativo",
    cep: parceiro.cep ?? "",
    bairro: parceiro.bairro ?? "",
    cidade: parceiro.cidade ?? "",
    uf: parceiro.uf ?? "",
    nu_endereco: parceiro.nu_endereco ?? parceiro.nuEndereco ?? "",
    usuarioId: parceiro.usuarioId ?? parceiro.usuario?.id ?? null,
    latitude: parceiro.latitude ?? null,
    longitude: parceiro.longitude ?? null,
  };
}

export async function listarParceirosPaginado({
  page = 0,
  size = 20,
  q = "",
  ufs = [],
  cidades = [],
  responsavelIds = [],
  statuses = [],
} = {}) {
  const data = await getPage("/api/parceiros", {
    page,
    size,
    q: q || undefined,
    ufs: serializeListParam(ufs),
    cidades: serializeListParam(cidades),
    responsavelIds: serializeListParam(responsavelIds),
    statuses: serializeListParam(statuses),
  });
  return {
    ...data,
    content: (data.content || []).map(normalizeParceiroResponse),
  };
}

export async function listarParceirosFiltros({
  q = "",
  ufs = [],
  cidades = [],
  responsavelIds = [],
  statuses = [],
} = {}) {
  const resposta = await httpClient.get("/api/parceiros/filtros", {
    params: {
      q: q || undefined,
      ufs: serializeListParam(ufs),
      cidades: serializeListParam(cidades),
      responsavelIds: serializeListParam(responsavelIds),
      statuses: serializeListParam(statuses),
    },
  });

  return {
    ufs: Array.isArray(resposta.data?.ufs) ? resposta.data.ufs : [],
    cidades: Array.isArray(resposta.data?.cidades) ? resposta.data.cidades : [],
    statuses: Array.isArray(resposta.data?.statuses) ? resposta.data.statuses : [],
  };
}

export async function listarParceiros() {
  const firstPage = await listarParceirosPaginado({ page: 0, size: 200 });
  const totalPages = Number(firstPage.totalPages || 0);

  if (totalPages <= 1) {
    return firstPage.content;
  }

  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      listarParceirosPaginado({ page: index + 1, size: 200 })
    )
  );

  return [firstPage, ...remainingPages].flatMap((page) => page.content || []);
}

export async function listarTodosParceirosFiltrados({
  q = "",
  ufs = [],
  cidades = [],
  responsavelIds = [],
  statuses = [],
  size = 200,
} = {}) {
  const firstPage = await listarParceirosPaginado({
    page: 0,
    size,
    q,
    ufs,
    cidades,
    responsavelIds,
    statuses,
  });
  const totalPages = Number(firstPage.totalPages || 0);

  if (totalPages <= 1) {
    return firstPage.content || [];
  }

  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      listarParceirosPaginado({
        page: index + 1,
        size,
        q,
        ufs,
        cidades,
        responsavelIds,
        statuses,
      })
    )
  );

  return [firstPage, ...remainingPages].flatMap((pageData) => pageData.content || []);
}

export async function pesquisarParceirosPaginado({
  q,
  page = 0,
  size = 20,
} = {}) {
  return getPage("/api/parceiros/search", { q, page, size });
}

export async function buscarParceiro(id) {
  const resposta = await httpClient.get(`/api/parceiros/${id}`);
  return normalizeParceiroResponse(resposta.data);
}

export async function listarHistoricoParceiro(
  id,
  { page = 0, size = 50, termo = "", categorias = [], dataInicio = "", dataFim = "" } = {}
) {
  const data = await getPage(`/api/parceiros/${id}/historico`, {
    page,
    size,
    termo: termo || undefined,
    categorias: serializeListParam(categorias),
    dataInicio: dataInicio || undefined,
    dataFim: dataFim || undefined,
  });

  return {
    ...data,
    content: Array.isArray(data.content) ? data.content : [],
  };
}

export async function salvarParceiro(parceiro) {
  const resposta = await httpClient.post(
    "/api/parceiros",
    normalizeParceiroPayload(parceiro),
    {
      headers: { "X-Idempotency-Key": generateIdempotencyKey("partner-create") },
    }
  );
  return resposta.data;
}

export async function atualizarParceiro(id, parceiro) {
  const resposta = await httpClient.put(
    `/api/parceiros/${id}`,
    normalizeParceiroPayload(parceiro),
    {
      headers: { "X-Idempotency-Key": generateIdempotencyKey(`partner-update-${id}`) },
    }
  );
  return normalizeParceiroResponse(resposta.data);
}
